// frontend/src/components/wizard/StepTarget.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ServerOff, Loader2, ArrowRight, ArrowLeft,
  CheckCircle2, AlertTriangle, RefreshCw, Info,
  AlertCircle, ShieldCheck, Copy, Eye, EyeOff,
  Wrench, ChevronDown, ChevronUp
} from 'lucide-react';
import { useWizardStore } from '../../store/useWizardStore';
import { useLanguageStore } from '../../store/useLanguageStore';
import { apiClient } from '../../api/client';
import { EngineSelector, DatabaseForm, GoogleSheetForm, FlatFileForm } from './ConnectionComponents';
import { generateDiagnosticRemediation } from '../../utils/dbaRequestGenerator';

// ── Tokens para nombre dinámico de archivo/hoja ──────────────────────────────
const PATTERN_TOKENS = [
  { token: '{YYYY}', example: '2026' },
  { token: '{MM}', example: '05' },
  { token: '{DD}', example: '17' },
  { token: '{HH}', example: '09' },
  { token: '{mm}', example: '00' },
  { token: '{ss}', example: '00' },
  { token: '{pipeline_name}', example: 'MiPipeline' },
];

function resolvePatternPreview(pattern) {
  if (!pattern) return '';
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return pattern
    .replace('{YYYY}', now.getFullYear())
    .replace('{MM}', pad(now.getMonth() + 1))
    .replace('{DD}', pad(now.getDate()))
    .replace('{HH}', pad(now.getHours()))
    .replace('{mm}', pad(now.getMinutes()))
    .replace('{ss}', pad(now.getSeconds()))
    .replace('{pipeline_name}', 'MiPipeline');
}

// ── Subcomponente: campo nombre + tokens + preview ────────────────────────────
function FileNameOrSheetField({ selectedTable, selectedEngine, isSheet, onChangeTable, onChangePattern, t }) {
  const ext = isSheet ? '' : (selectedEngine === 'excel' ? '.xlsx' : selectedEngine === 'json' ? '.json' : `.${selectedEngine}`);
  const placeholder = isSheet
    ? (t('wizard.step_target.placeholder_sheet') || 'Ej: resultado  ó  {YYYY}{MM}{DD}_datos')
    : selectedEngine === 'excel'
      ? (t('wizard.step_target.placeholder_excel') || 'Ej: reporte.xlsx  ó  {YYYY}{MM}{DD}_reporte.xlsx')
      : (t('wizard.step_target.placeholder_csv') || 'Ej: reporte.csv  ó  {YYYY}{MM}{DD}_reporte.csv');

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 block mb-1">
        {isSheet
          ? (t('wizard.step_target.sheet_name') || 'Nombre de la hoja')
          : (t('wizard.step_target.file_name') || 'Nombre del archivo')
        }
      </label>
      <input
        type="text"
        value={selectedTable}
        onChange={(e) => {
          onChangeTable(e.target.value);
          onChangePattern(e.target.value);
        }}
        placeholder={placeholder}
        className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-emerald-600 outline-none font-mono text-sm"
      />

      {/* Tokens clickeables */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {PATTERN_TOKENS.map(({ token }) => (
          <button
            key={token}
            type="button"
            onClick={() => {
              const next = (selectedTable || '') + token;
              onChangeTable(next);
              onChangePattern(next);
            }}
            className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-emerald-100 hover:text-emerald-700 text-slate-600 text-[11px] font-mono border border-slate-200 transition"
          >
            {token}
          </button>
        ))}
      </div>

      {/* Preview en vivo si hay tokens */}
      {selectedTable?.includes('{') && (
        <p className="mt-2 text-xs text-slate-500 flex items-center gap-1">
          <span className="text-slate-400 font-medium">
            {t('wizard.step_target.pattern_preview') || 'Preview:'}
          </span>
          <span className="font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
            {resolvePatternPreview(selectedTable)}{ext}
          </span>
        </p>
      )}

      <p className="text-[11px] text-slate-400 mt-1.5">
        {t('wizard.step_target.pattern_desc') || 'Opcional. Si se deja vacío, se genera automáticamente.'}
      </p>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function StepTarget() {
  const {
    target, setTarget, setTargetColumns, setMapping, nextStep, prevStep, source, syncMode, setSyncMode,
    targetSelectedTables, addTargetTable, removeTargetTable, reorderTargetTables,
    sourceSelectedTables, validateTableCount,
    editId,
  } = useWizardStore();
  const { t } = useLanguageStore();

  const getSupportedJoins = async (engineType) => {
    try {
      const res = await apiClient.get(`/catalogs/engines/${engineType}`);
      return res.data?.config?.supported_joins || ['INNER', 'LEFT', 'RIGHT', 'FULL'];
    } catch (e) {
      return ['INNER', 'LEFT', 'RIGHT', 'FULL'];
    }
  };

  const trimmedStr = z.string().optional().transform(v => v?.trim() ?? '');

  // ── Esquema Zod ───────────────────────────────────────────────────
  const targetSchema = useMemo(() => z.object({
    syncMode: z.enum(['append', 'overwrite', 'upsert']).default('append'),
    engineType: z.string(),
    host: trimmedStr,
    port: trimmedStr,
    user: trimmedStr,
    password: trimmedStr,
    database: trimmedStr,
    includeHeaders: z.boolean().default(true),
    fileNamePattern: z.string().optional().default(''),
    sheetTabName: z.string().optional().default(''),
    uploadMethod: trimmedStr,
    useSsh: z.boolean().optional().default(false),
    ssh_host: trimmedStr,
    ssh_port: z.string().optional().default('22'),
    ssh_user: trimmedStr,
    ssh_pkey_file: z.any().optional(),
    ssh_pkey_content: trimmedStr,
    ssh_remote_bind_host: z.string().optional().default('localhost'),
    networkType: z.string().optional().default('cloud'),
    connectionMethod: z.enum(['ssh', 'firewall', 'direct']).optional().default('firewall'),
    warehouse: trimmedStr,
    schema: trimmedStr,
    auth_type: z.string().optional().default('none'),
    api_key_name: trimmedStr,
    api_key_location: z.string().optional().default('header'),
    token_url: trimmedStr,
    pagination_type: z.string().optional().default('auto'),
    param_offset: trimmedStr,
    param_limit: trimmedStr,
    param_page: trimmedStr,
    param_per_page: trimmedStr,
    cursor_field: trimmedStr,
    cursor_param: trimmedStr,
    page_size: trimmedStr,
    data_path: trimmedStr,
    custom_headers: trimmedStr,
    jsonOutputFormat: z.enum(['array', 'ndjson']).default('array'),
  }).superRefine((data, ctx) => {
    if (['mysql', 'postgres', 'oracle', 'mssql', 'mongodb', 'odoo', 'sap_hana', 'mariadb', 'azure_sql', 'salesforce', 'snowflake', 'redshift'].includes(data.engineType)) {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.host_req'), code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!['salesforce', 'hubspot'].includes(data.engineType) && !data.database) {
        ctx.addIssue({ path: ['database'], message: t('wizard.validation.db_req'), code: 'custom' });
      }
      if (data.engineType === 'snowflake' && !data.warehouse) {
        ctx.addIssue({ path: ['warehouse'], message: t('wizard.validation.warehouse_req') || 'Warehouse es requerido', code: 'custom' });
      }
    }
    if (data.engineType === 'googlesheets' && !data.host) {
      ctx.addIssue({ path: ['host'], message: t('wizard.validation.id_req'), code: 'custom' });
    }
    // BigQuery → solo dataset requerido (auth via service account JSON en el servidor)
    if (data.engineType === 'bigquery' && !data.database) {
      ctx.addIssue({ path: ['database'], message: t('wizard.validation.dataset_req') || 'Dataset es requerido', code: 'custom' });
    }
    // REST API → host (URL Base) + database (Endpoint) requeridos + auth-specific
    if (data.engineType === 'rest_api') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.base_url_req') || 'URL Base es requerida', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.endpoint_req') || 'Endpoint es requerido', code: 'custom' });
      if (data.auth_type === 'api_key' && !data.password) {
        ctx.addIssue({ path: ['password'], message: t('wizard.validation.api_key_req') || 'API Key es requerido', code: 'custom' });
      }
      if (data.auth_type === 'bearer' && !data.password) {
        ctx.addIssue({ path: ['password'], message: t('wizard.validation.token_req') || 'Token es requerido', code: 'custom' });
      }
      if (data.auth_type === 'basic') {
        if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
        if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      }
      if (data.auth_type === 'oauth2_cc') {
        if (!data.user) ctx.addIssue({ path: ['user'], message: 'Client ID es requerido', code: 'custom' });
        if (!data.password) ctx.addIssue({ path: ['password'], message: 'Client Secret es requerido', code: 'custom' });
        if (!data.token_url) ctx.addIssue({ path: ['token_url'], message: 'Token URL es requerida', code: 'custom' });
      }
    }
    // HubSpot → solo Access Token (password) requerido
    if (data.engineType === 'hubspot') {
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.token_req') || 'Access Token es requerido', code: 'custom' });
    }
    // Dynamics 365 → host (Org URL), user (Client ID), password (Client Secret), database (Tenant ID)
    if (data.engineType === 'dynamics365') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.org_url_req') || 'URL de la organización es requerida', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.tenant_id_req') || 'Tenant ID es requerido', code: 'custom' });
    }
    // Zoho CRM → host (Accounts URL), user (Client ID), password (Client Secret), database (Refresh Token)
    if (data.engineType === 'zoho_crm') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.zoho_domain_req') || 'Zoho Domain es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.refresh_token_req') || 'Refresh Token es requerido', code: 'custom' });
    }
    // Zoho Books → mismos campos que Zoho CRM (OAuth 2.0 Self Client)
    if (data.engineType === 'zoho_books') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.zoho_domain_req') || 'Zoho Domain es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.refresh_token_req') || 'Refresh Token es requerido', code: 'custom' });
    }

    // Zoho Inventory → mismos campos que Zoho CRM (OAuth 2.0 Self Client)
    if (data.engineType === 'zoho_inventory') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.zoho_domain_req') || 'Zoho Domain es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.refresh_token_req') || 'Refresh Token es requerido', code: 'custom' });
    }

    // SugarCRM → host (Instance URL), user (Username), password (Password), database (Client ID), port (Client Secret)
    if (data.engineType === 'sugarcrm') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.instance_url_req') || 'Instance URL es requerida', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
    }

    // NetSuite → host (Account ID), user (Consumer Key), password (Consumer Secret), database (Token Key), port (Token Secret)
    if (data.engineType === 'netsuite') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.account_id_req') || 'Account ID es requerido', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.consumer_key_req') || 'Consumer Key es requerida', code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.consumer_secret_req') || 'Consumer Secret es requerido', code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.token_key_req') || 'Token Key es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.token_secret_req') || 'Token Secret es requerido', code: 'custom' });
    }

    // SuiteCRM → host (Instance URL), user (Username), password (Password), database (Client ID), port (Client Secret)
    if (data.engineType === 'suitecrm') {
      if (!data.host) ctx.addIssue({ path: ['host'], message: t('wizard.validation.instance_url_req') || 'Instance URL es requerida', code: 'custom' });
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.client_id_req') || 'Client ID es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.client_secret_req') || 'Client Secret es requerido', code: 'custom' });
    }

    // Defontana → host (Ambiente), user (User/Email), password (Password), database (Company), port (Client code)
    if (data.engineType === 'defontana') {
      if (!data.user) ctx.addIssue({ path: ['user'], message: t('wizard.validation.user_req'), code: 'custom' });
      if (!data.password) ctx.addIssue({ path: ['password'], message: t('wizard.validation.pass_req'), code: 'custom' });
      if (!data.database) ctx.addIssue({ path: ['database'], message: t('wizard.validation.company_code_req') || 'Código de empresa es requerido', code: 'custom' });
      if (!data.port) ctx.addIssue({ path: ['port'], message: t('wizard.validation.client_code_req') || 'Código de cliente es requerido', code: 'custom' });
    }

    if (data.connectionMethod === 'ssh' && data.networkType === 'local') {
      if (!data.ssh_host) ctx.addIssue({ path: ['ssh_host'], message: t('wizard.validation.ssh_host_req') || 'SSH Host es requerido', code: 'custom' });
      if (!data.ssh_user) ctx.addIssue({ path: ['ssh_user'], message: t('wizard.validation.ssh_user_req') || 'SSH User es requerido', code: 'custom' });
      if (!data.ssh_pkey_content && !data.ssh_pkey_file) {
        ctx.addIssue({ path: ['ssh_pkey_file'], message: t('wizard.validation.ssh_key_req') || 'SSH Private Key es requerida', code: 'custom' });
      }
    }
  }), [t]);

  // ── Estado ────────────────────────────────────────────────────────
  const [isTesting, setIsTesting] = useState(false);
  const [testError, setTestError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(true);
  const [isDynamicSchema, setIsDynamicSchema] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [networkType, setNetworkType] = useState('cloud');
  const [showSshFields, setShowSshFields] = useState(false);

  const [connectionSuccess, setConnectionSuccess] = useState(() => {
    if (['csv', 'excel', 'googlesheets', 'dbf', 'json'].includes(target?.type)) return true;
    if (Array.isArray(target?.config?.availableTables) && target.config.availableTables.length > 0) return true;
    if (targetSelectedTables.length > 0) return true;
    if (editId && target?.config?.selectedTable) return true;
    return false;
  });

  const [availableTables, setAvailableTables] = useState(() => {
    const stored = target?.config?.availableTables;
    if (Array.isArray(stored) && stored.length > 0) return stored;
    if (editId && targetSelectedTables.length > 0) return targetSelectedTables;
    if (editId && target?.config?.selectedTable) return [target.config.selectedTable];
    return [];
  });

  const [selectedTable, setSelectedTable] = useState(() => {
    return useWizardStore.getState().target?.config?.selectedTable || '';
  });

  const [searchEngine, setSearchEngine] = useState('');
  const [expandedCats, setExpandedCats] = useState(['databases', 'files']);

  // ── React Hook Form ───────────────────────────────────────────────
  const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm({
    resolver: zodResolver(targetSchema),
    defaultValues: {
      engineType: target?.type || 'mysql',
      host: target?.config?.host || '',
      port: target?.config?.port || '',
      user: target?.config?.user || '',
      password: target?.config?.password || '',
      database: target?.config?.database || '',
      syncMode: syncMode || 'append',
      includeHeaders: target?.config?.includeHeaders ?? true,
      fileNamePattern: target?.config?.fileNamePattern || '',
      sheetTabName: target?.config?.sheetTabName || '',
      uploadMethod: 'url',
      useSsh: target?.config?.useSsh || false,
      ssh_host: target?.config?.ssh_host || '',
      ssh_port: target?.config?.ssh_port || '22',
      ssh_user: target?.config?.ssh_user || '',
      ssh_pkey_content: target?.config?.ssh_pkey_content || '',
      ssh_remote_bind_host: target?.config?.ssh_remote_bind_host || 'localhost',
      networkType: 'cloud',
      connectionMethod: target?.config?.connectionMethod || 'firewall',
    },
  });

  // ── Restaurar selectedTable desde store ───────────────────────────
  useEffect(() => {
    const tableToRestore = useWizardStore.getState().target?.config?.selectedTable || '';
    if (tableToRestore !== selectedTable) setSelectedTable(tableToRestore);
  }, [target?.config?.selectedTable]);

  const selectedEngine = watch('engineType');
  const prevEngineRef = useRef(target?.type || 'mysql');
  const prevConnectionSuccessRef = useRef(false);

  // ── Derivar type_motor desde catálogo ────────────────────────────
  const selectedEngineData = useMemo(
    () => categories.flatMap(c => c.options).find(o => o.engine_key === selectedEngine),
    [categories, selectedEngine]
  );
  const selectedTypeMotor = selectedEngineData?.type_motor;
  const isTargetFlat = ['csv', 'excel', 'googlesheets', 'dbf', 'json'].includes(selectedEngine);
  // const isAppendOnly = selectedEngine === 'rest_api';
  const isAppendOnly = ['rest_api', 'defontana'].includes(selectedEngine);

  // Forzar syncMode a append si es flat y tenía upsert
  useEffect(() => {
    if (isTargetFlat && watch('syncMode') === 'upsert') setValue('syncMode', 'append');
    if (isAppendOnly && watch('syncMode') !== 'append') setValue('syncMode', 'append');
  }, [isTargetFlat]);

  useEffect(() => {
    if (targetSelectedTables.length > 0 && !selectedTable) {
      setAvailableTables(target?.config?.availableTables || []);
    }
  }, [targetSelectedTables, target?.config?.availableTables]);

  // ── Cargar catálogo de motores ────────────────────────────────────
  useEffect(() => {
    apiClient.get('/catalogs/engines?type=target')
      .then(res => { setCategories(res.data); setIsLoadingCatalogs(false); });
  }, []);

  // ── Resetear al cambiar de motor ──────────────────────────────────
  useEffect(() => {
    if (isLoadingCatalogs || categories.length === 0) return;
    const foundConfig = selectedEngineData?.config_default || {};
    const currentPort = getValues('port');
    const hasExistingConnection =
      (target?.config?.availableTables?.length ?? 0) > 0 ||
      Boolean(editId && (target?.config?.selectedTable || targetSelectedTables.length > 0));

    if (!currentPort && foundConfig.default_port && !hasExistingConnection) {
      setValue('port', foundConfig.default_port, { shouldDirty: false });
    }

    if (selectedEngine !== prevEngineRef.current) {
      prevEngineRef.current = selectedEngine;
      setConnectionSuccess(false);
      setTestError(null);
      setValue('host', ''); setValue('database', ''); setValue('password', '');
      setValue('user', ''); setValue('port', foundConfig.default_port || '');
      setSelectedTable(''); setAvailableTables([]);

      const targetState = useWizardStore.getState();
      targetState.setTarget({
        type: targetState.target.type,
        config: { ...targetState.target.config, selectedTable: '' }
      });
      targetState.clearSelectedTables('target');

      setValue('useSsh', false); setValue('ssh_host', ''); setValue('ssh_port', '22');
      setValue('ssh_user', ''); setValue('ssh_pkey_content', ''); setValue('ssh_pkey_file', null);
      setValue('ssh_remote_bind_host', 'localhost');
      setValue('networkType', 'cloud'); setValue('connectionMethod', 'firewall');
    }
  }, [selectedEngine, categories, isLoadingCatalogs, selectedEngineData, setValue, getValues]);

  // ── Invalidar conexión al editar credenciales ─────────────────────
  useEffect(() => {
    const sub = watch((_, { name }) => {
      if (['host', 'port', 'user', 'password', 'database',
        'ssh_host', 'ssh_port', 'ssh_user', 'ssh_pkey_content', 'ssh_remote_bind_host'].includes(name)) {
        setConnectionSuccess(false);
        setTestError(null);
        setIsDynamicSchema(false);
      }
    });
    return () => sub.unsubscribe();
  }, [watch]);

  // ── Sincronizar networkType / SSH ─────────────────────────────────
  useEffect(() => {
    const formNetworkType = watch('networkType');
    const formConnectionMethod = watch('connectionMethod');
    if (formNetworkType !== networkType) setNetworkType(formNetworkType);
    setShowSshFields(formNetworkType === 'local' && formConnectionMethod === 'ssh');
  }, [watch('networkType'), networkType, watch('connectionMethod'), watch]);

  const useSshValue = watch('useSsh');
  const connectionMethodValue = watch('connectionMethod');
  const hostValue = watch('host');
  const portValue = watch('port');
  const userValue = watch('user');
  const passwordValue = watch('password');
  const uploadMethodValue = watch('uploadMethod');

  useEffect(() => { return () => { prevConnectionSuccessRef.current = false; }; }, []);

  // ── Reset si el usuario edita credenciales activamente ────────────
  useEffect(() => {
    if (!connectionSuccess) { prevConnectionSuccessRef.current = false; return; }
    if (!prevConnectionSuccessRef.current) { prevConnectionSuccessRef.current = true; return; }
    setTestError(null); setConnectionSuccess(false);
    setSelectedTable(''); setAvailableTables([]);
  }, [useSshValue, connectionMethodValue, networkType,
    hostValue, portValue, userValue, passwordValue, uploadMethodValue]);

  // ── Restaurar tablas múltiples si vienen del store ────────────────
  useEffect(() => {
    const { targetSelectedTables: tst, target: tgt } = useWizardStore.getState();
    if (tst.length > 0) {
      setAvailableTables(tgt?.config?.availableTables || []);
      setConnectionSuccess(true);
    }
  }, []);

  // ── Copiar config desde origen ────────────────────────────────────
  const handleCopyFromSource = () => {
    if (source?.config) {
      setValue('host', source.config.host || '');
      setValue('port', source.config.port || '');
      setValue('user', source.config.user || '');
      setValue('database', source.config.database || '');
      setValue('networkType', source.config.networkType || 'cloud');
      setValue('useSsh', source.config.useSsh || false);
      setValue('ssh_host', source.config.ssh_host || '');
      setValue('ssh_port', source.config.ssh_port || '22');
      setValue('ssh_user', source.config.ssh_user || '');
      setValue('ssh_pkey_content', source.config.ssh_pkey_content || '');
      setValue('ssh_remote_bind_host', source.config.ssh_remote_bind_host || 'localhost');
      setValue('password', '');
      setConnectionSuccess(false); setTestError(null);
      setNetworkType(source.config.networkType || 'cloud');
    }
  };

  // ── Parsear Google Sheets URL → ID ───────────────────────────────
  const handleHostChange = (e) => {
    let val = e.target.value;
    if (selectedEngine === 'googlesheets') {
      const match = val.match(/\/d\/([a-zA-Z0-9-_]+)/);
      if (match?.[1]) val = match[1];
    }
    setValue('host', val, { shouldValidate: true });
    setConnectionSuccess(false); setTestError(null);
  };

  // ── handleTableSelect ─────────────────────────────────────────────
  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    const { target: tgt } = useWizardStore.getState();
    if (tableName !== tgt?.config?.selectedTable) {
      setTargetColumns([]);
      setMapping([]);
    }
    useWizardStore.getState().setTarget({
      type: tgt.type,
      config: { ...tgt.config, selectedTable: tableName, availableTables },
      supportedJoins: tgt.supportedJoins
    });
  };

  // ── onSubmit: validar conexión ────────────────────────────────────
  const onSubmit = async (data) => {
    Object.keys(data).forEach(k => { if (typeof data[k] === 'string') data[k] = data[k].trim(); });
    setIsTesting(true); setTestError(null); setIsDynamicSchema(false);

    try {
      // FLAT / SAAS: sin test al servidor
      if (selectedTypeMotor === 'FLAT' || selectedTypeMotor === 'NONE') {
        const supportedJoins = [];
        setTarget({
          type: data.engineType,
          config: { ...data, selectedTable, availableTables },
          supportedJoins
        });
        setAvailableTables(['']);
        setConnectionSuccess(true);
        return;
      }

      // BD: leer SSH key si viene como File
      if (data.ssh_pkey_file && typeof data.ssh_pkey_file === 'object') {
        const sshKeyText = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(data.ssh_pkey_file);
        });
        data.ssh_pkey_content = sshKeyText; data.useSsh = true;
      }

      if (data.networkType === 'cloud' || (data.networkType === 'local' && data.connectionMethod !== 'ssh')) {
        data.ssh_host = ''; data.ssh_port = ''; data.ssh_user = '';
        data.ssh_pkey_content = ''; data.ssh_remote_bind_host = ''; data.useSsh = false;
      }

      const res = await apiClient.post('/onboarding/test-connection', {
        engine: data.engineType,
        ...data,
        // 🔒 Sprint 12: credential masking — enviar pipeline ID en edit mode
        ...(editId ? { idConfigHead: editId, side: 'target' } : {}),
      });
      const supportedJoins = await getSupportedJoins(data.engineType);

      if (res.data.success) {
        const finalSelectedTable = selectedTable || '';
        setTarget({
          type: data.engineType,
          config: { ...data, availableTables: res.data.tables, selectedTable: finalSelectedTable },
          supportedJoins
        });
        if (res.data.columns) setTargetColumns(res.data.columns);
        setAvailableTables(res.data.tables || []);
        setConnectionSuccess(true);
      } else {
        const diagnostic = res.data.error_diagnostic || null;
        let diagnosticText = '';
        if (diagnostic) {
          const formData = watch();
          diagnosticText = generateDiagnosticRemediation({
            diagnosticCode: diagnostic,
            engineType: formData.engineType,
            host: formData.host,
            port: formData.port,
            database: formData.database,
            user: formData.user,
            connectionMethod: formData.connectionMethod,
            sshHost: formData.ssh_host,
            t,
          });
        }
        setTestError({
          title: res.data.error_type === 'ssh_validation'
            ? 'Validación SSH'
            : (t('wizard.errors.conn_failed_target') || 'No pudimos conectar con tu Destino'),
          description: t(`errors.${res.data.error}`, res.data.error_params || {}) !== `errors.${res.data.error}`
            ? t(`errors.${res.data.error}`, res.data.error_params || {})
            : (res.data.error || t('wizard.errors.unknown') || 'Error desconocido'),
          diagnostic: diagnosticText,
          diagnosticCode: diagnostic,
        });
      }
    } catch (err) {
      setTestError({
        title: t('wizard.errors.conn_failed_target') || 'No pudimos conectar con tu Destino',
        description: err.response?.data?.detail || 'Error de red.',
      });
    } finally {
      setIsTesting(false);
    }
  };

  // ── handleContinue ────────────────────────────────────────────────
  const handleContinue = async () => {
    setIsTesting(true);
    const data = watch();

    // 🆕 SSH: leer archivo .pem como texto (mismo patrón que handleTestConnection)
    if (data.ssh_pkey_file && data.connectionMethod === 'ssh' && data.networkType === 'local') {
      const fileObj = Array.isArray(data.ssh_pkey_file)
        ? data.ssh_pkey_file[0]
        : data.ssh_pkey_file?.[0] || data.ssh_pkey_file;

      if (fileObj && fileObj instanceof File) {
        const sshKeyText = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(fileObj);
        });
        data.ssh_pkey_content = sshKeyText;
        data.useSsh = true;
      }
    }

    const sourceSelectedTablesStore = useWizardStore.getState().sourceSelectedTables || [];
    const sourceCount = sourceSelectedTablesStore.length ||
      (useWizardStore.getState().source?.config?.selectedTable ? 1 : 0);
    const targetCount = targetSelectedTables.length || (selectedTable ? 1 : 0);

    if (sourceCount > 1 && targetCount > 1 && sourceCount !== targetCount) {
      setTestError({
        title: t('wizard.errors.table_count_mismatch') || 'Cantidad de tablas',
        description: t('wizard.errors.table_count_desc') ||
          `Se requieren ${sourceCount} tablas destino, pero tienes ${targetCount}.`,
      });
      setIsTesting(false); return;
    }

    const isMultiTable = sourceCount > 1 || targetCount > 1;

    // Leer columnas solo para BD en modo 1:1
    if (!isMultiTable && !isTargetFlat) {
      try {
        const res = await apiClient.post('/onboarding/columns', {
          type: data.engineType,
          config: {
            ...data,
            selectedTable,
            ...(editId ? { _idConfigHead: editId, _side: 'target' } : {}),
          },
        });
        if (res.data.success && res.data.columns) setTargetColumns(res.data.columns);
      } catch (e) {
        setIsTesting(false);
        const isEphemeral = e.response?.data?.detail?.code === 'ERR_EPHEMERAL_FILE';
        setTestError({
          title: isEphemeral
            ? (t('wizard.errors.ephemeral_file_title') || 'Archivo no disponible')
            : (t('wizard.errors.schema_error') || 'Error de Esquema'),
          description: isEphemeral
            ? (t('wizard.errors.ephemeral_file_desc') || 'Este archivo fue eliminado tras la ejecución anterior (ZDR). Sube el archivo nuevamente.')
            : (t('wizard.errors.schema_desc') || 'No se pudieron leer las columnas de esta tabla.'),
        });
        return;
      }
    }

    const supportedJoins = await getSupportedJoins(data.engineType);
    const finalSelectedTable = selectedTable || '';

    setTarget({
      type: data.engineType,
      config: { ...data, selectedTable: finalSelectedTable, availableTables },
      supportedJoins
    });

    const savedTable = useWizardStore.getState().target?.config?.selectedTable;
    if (!isMultiTable && !savedTable && !isTargetFlat) {
      setTestError({ title: t('wizard.step_target.table_required') || 'Tabla requerida', description: t('wizard.step_target.table_required_desc') || 'Debes seleccionar una tabla antes de continuar.' });
      setIsTesting(false); return;
    }

    setSyncMode(data.syncMode);
    setIsTesting(false);
    nextStep();
  };

  // ── handleBack ────────────────────────────────────────────────────
  const handleBack = async () => {
    const data = getValues();
    const supportedJoins = await getSupportedJoins(data.engineType);
    setTarget({
      type: data.engineType,
      config: {
        ...target?.config, ...data,
        selectedTable: target?.config?.selectedTable,
        availableTables: target?.config?.availableTables
      },
      supportedJoins
    });
    if (data.syncMode) setSyncMode(data.syncMode);
    prevStep();
  };

  // ─────────────────────────────────────────────────────────────────
  if (isLoadingCatalogs) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  }

  const toggleCategory = (key) =>
    setExpandedCats(prev => prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]);

  const showDatabaseForm = selectedTypeMotor === 'SQL' || selectedTypeMotor === 'NOSQL' || selectedTypeMotor === 'API';
  const showGoogleForm = selectedEngine === 'googlesheets';
  const showFlatForm = selectedTypeMotor === 'FLAT' && !showGoogleForm;
  const showTableSelect = showDatabaseForm;
  const showSheetInput = selectedEngine === 'googlesheets';
  const showFileNameInput = ['csv', 'excel', 'dbf', 'json'].includes(selectedEngine);

  // Helper: resumen del nombre resuelto para el panel confirmado
  const resolvedFileName = (ext) =>
    selectedTable?.includes('{')
      ? resolvePatternPreview(selectedTable) + ext
      : selectedTable;

  return (
    <div className="w-full max-w-3xl mx-auto pb-12">
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-slate-900">{t('wizard.step_target.title')}</h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 animate-in fade-in slide-in-from-bottom-2">

        {/* ── Selector de Motor ──────────────────────────────────── */}
        <EngineSelector
          categories={categories}
          searchEngine={searchEngine}
          setSearchEngine={setSearchEngine}
          expandedCats={expandedCats}
          toggleCategory={toggleCategory}
          register={register}
          selectedEngine={selectedEngine}
          setValue={setValue}
        />

        {/* ── Formulario según type_motor ───────────────────────── */}
        {showDatabaseForm && (
          <DatabaseForm
            register={register}
            errors={errors}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            watch={watch}
            setValue={setValue}
            onTrimField={(name, value) => setValue(name, value.trim(), { shouldValidate: false })}
            isTarget={true}
            onCopyFromSource={handleCopyFromSource}
            isApiEngine={selectedTypeMotor === 'API'}
            networkType={networkType}
            onNetworkTypeChange={(type) => { setNetworkType(type); setValue('networkType', type); }}
            connectionMethod={watch('connectionMethod')}
            onConnectionMethodChange={(method) => setValue('connectionMethod', method)}
          />
        )}

        {showGoogleForm && (
          <GoogleSheetForm
            register={register}
            errors={errors}
            handleHostChange={handleHostChange}
            isTarget={true}
            onTrimField={(name, value) => setValue(name, value.trim(), { shouldValidate: false })}
          />
        )}

        {showFlatForm && (
          <FlatFileForm
            register={register}
            errors={errors}
            isTarget={true}
            engineType={selectedEngine}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            watch={watch}
            setValue={setValue}
          />
        )}

        {/* Nombre del archivo — CSV / Excel / DBF */}
        {showFileNameInput && (
          <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
            <FileNameOrSheetField
              selectedTable={selectedTable}
              selectedEngine={selectedEngine}
              isSheet={false}
              onChangeTable={handleTableSelect}
              onChangePattern={(val) => setValue('fileNamePattern', val, { shouldDirty: true })}
              t={t}
            />
          </div>
        )}

        {/* ── Selector formato JSON (Array vs NDJSON) ────────────── */}
        {selectedEngine === 'json' && (
          <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm space-y-3">
            <label className="text-sm font-bold text-slate-900 block">
              {t('wizard.step_target.json_format')}
            </label>
            <div className="flex gap-3">
              <label className={`flex-1 p-3 border rounded-lg cursor-pointer transition-all ${watch('jsonOutputFormat') === 'array' ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'}`}>
                <input
                  type="radio"
                  {...register('jsonOutputFormat')}
                  value="array"
                  className="sr-only"
                />
                <span className="text-sm font-semibold text-slate-800">JSON Array</span>
                <p className="text-xs text-slate-500 mt-1">{t('wizard.step_target.json_format_array_desc')}</p>
              </label>
              <label className={`flex-1 p-3 border rounded-lg cursor-pointer transition-all ${watch('jsonOutputFormat') === 'ndjson' ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'}`}>
                <input
                  type="radio"
                  {...register('jsonOutputFormat')}
                  value="ndjson"
                  className="sr-only"
                />
                <span className="text-sm font-semibold text-slate-800">NDJSON</span>
                <p className="text-xs text-slate-500 mt-1">{t('wizard.step_target.json_format_ndjson_desc')}</p>
              </label>
            </div>
          </div>
        )}

        {/* Nombre de pestaña — Excel únicamente, bloque independiente */}
        {showFileNameInput && selectedEngine === 'excel' && (
          <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
            <label className="text-sm font-medium text-slate-700 block mb-1">
              {t('wizard.step_target.sheet_tab_name') || 'Nombre de la pestaña (hoja)'}
              <span className="text-slate-400 font-normal ml-1">
                ({t('common.optional') || 'opcional'})
              </span>
            </label>
            <input
              type="text"
              {...register('sheetTabName')}
              placeholder={t('wizard.step_target.placeholder_tab') || 'Ej: Datos  ó  {YYYY}{MM}{DD}_Datos'}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-emerald-600 outline-none text-sm"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PATTERN_TOKENS.map(({ token }) => (
                <button
                  key={token}
                  type="button"
                  onClick={() => {
                    const cur = watch('sheetTabName') || '';
                    setValue('sheetTabName', cur + token, { shouldDirty: true });
                  }}
                  className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-emerald-100 hover:text-emerald-700 text-slate-600 text-[11px] font-mono border border-slate-200 transition"
                >
                  {token}
                </button>
              ))}
            </div>
            {watch('sheetTabName')?.includes('{') && (
              <p className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                <span className="text-slate-400 font-medium">
                  {t('wizard.step_target.pattern_preview') || 'Preview:'}
                </span>
                <span className="font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  {resolvePatternPreview(watch('sheetTabName'))}
                </span>
              </p>
            )}
            <p className="text-[11px] text-slate-400 mt-1.5">
              {t('wizard.step_target.tab_desc') || 'Opcional. Si vacío, se usa el nombre del archivo como pestaña.'}
            </p>
          </div>
        )}

        {/* ── Nombre de hoja — Google Sheets ─────────────────────
            Visible ANTES de presionar "Validar Destino"           */}
        {showSheetInput && (
          <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm space-y-4">
            <FileNameOrSheetField
              selectedTable={selectedTable}
              selectedEngine={selectedEngine}
              isSheet={true}
              onChangeTable={handleTableSelect}
              onChangePattern={(val) => setValue('fileNamePattern', val, { shouldDirty: true })}
              t={t}
            />
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" {...register('includeHeaders')} className="w-4 h-4 rounded accent-emerald-600" />
              <span className="text-sm font-medium text-slate-700">{t('wizard.forms.include_headers')}</span>
            </label>
            <p className="text-xs text-slate-400 ml-7">{t('wizard.forms.headers_desc')}</p>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────────── */}
        {testError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex">
              <ServerOff className="h-5 w-5 text-red-600 mr-3 shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-red-900">{testError.title}</h4>
                <p className="text-red-700 text-sm mt-1">{testError.description}</p>
              </div>
            </div>

            {/* 🆕 Panel de diagnóstico expandible */}
            {testError.diagnostic && (
              <DiagnosticPanel diagnostic={testError.diagnostic} t={t} />
            )}
          </div>
        )}

        {/* ── Botón Validar Destino ─────────────────────────────── */}
        {!connectionSuccess && (
          <div className="flex justify-between pt-6 border-t border-slate-200 mt-6">
            <button type="button" onClick={handleBack}
              className="px-5 py-2 hover:bg-slate-100 rounded-lg flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.buttons.back')}
            </button>
            <button type="submit" disabled={isTesting}
              className="px-6 py-2 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 flex items-center disabled:opacity-60">
              {isTesting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
              {t('wizard.step_target.validate_btn')}
            </button>
          </div>
        )}

        {/* ── Panel éxito FLAT (CSV / Excel / DBF) ─────────────── */}
        {connectionSuccess && isTargetFlat && !showSheetInput && (
          <div className="mt-5 p-6 bg-emerald-50 border border-emerald-100 rounded-2xl animate-in slide-in-from-bottom-4">
            <div className="flex items-center mb-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2" />
              <h3 className="text-lg font-bold">{t('wizard.step_target.confirmed')}</h3>
            </div>

            {selectedTable && (
              <p className="text-sm text-slate-600 mb-4">
                <span className="font-medium">{t('wizard.step_target.file_name')}:</span>{' '}
                <span className="font-mono text-emerald-700">
                  {resolvedFileName(selectedEngine === 'excel' ? '.xlsx' : `.${selectedEngine}`)}
                </span>
              </p>
            )}

            {/* syncMode */}
            <label className="text-sm font-medium block mb-3">{t('wizard.step_target.sync_strategy')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {[{ value: 'append', color: 'emerald' }, { value: 'overwrite', color: 'red' }]
                .map(({ value: mode, color }) => (
                  <label key={mode}
                    className={`relative p-4 border rounded-xl cursor-pointer transition-all ${watch('syncMode') === mode
                      ? `border-${color}-600 bg-white shadow-sm ring-1 ring-${color}-600`
                      : 'bg-white hover:bg-slate-50 border-slate-200'}`}>
                    <input type="radio" value={mode} {...register('syncMode')} className="sr-only" />
                    <span className="font-bold text-sm text-slate-800">{t(`wizard.sync_modes.${mode}`)}</span>
                    <p className="text-xs text-slate-500 mt-1">{t(`wizard.sync_modes.${mode}_desc`)}</p>
                    {watch('syncMode') === mode && <CheckCircle2 className={`absolute top-3 right-3 w-4 h-4 text-${color}-600`} />}
                  </label>
                ))}
            </div>

            {watch('syncMode') === 'append' && (
              <div className="mb-4 p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-800 rounded-r-lg flex items-start animate-in fade-in">
                <AlertTriangle className="w-5 h-5 mr-3 shrink-0" />
                <div>
                  <p className="font-bold text-sm">{t('wizard.alerts.append_title')}</p>
                  <p className="text-sm mt-1">{t('wizard.alerts.append_desc_flat')}</p>
                </div>
              </div>
            )}
            {watch('syncMode') === 'overwrite' && (
              <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-800 rounded-r-lg flex items-start animate-in fade-in">
                <AlertTriangle className="w-5 h-5 mr-3 shrink-0" />
                <div>
                  <p className="font-bold text-sm">⚠️ {t('wizard.alerts.overwrite_title')}</p>
                  <p className="text-sm mt-1">{t('wizard.alerts.overwrite_desc_flat')}</p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-emerald-200">
              <button type="button" onClick={handleBack}
                className="px-5 py-2 hover:bg-emerald-100 rounded-lg flex items-center text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" />{t('common.buttons.back')}
              </button>
              <button type="button" onClick={handleContinue} disabled={isTesting}
                className="px-6 py-2 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 flex items-center text-sm disabled:opacity-60">
                {isTesting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
                {t('wizard.step_target.continue_btn')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* ── Panel éxito GOOGLE SHEETS ─────────────────────────── */}
        {connectionSuccess && showSheetInput && (
          <div className="mt-5 p-6 bg-emerald-50 border border-emerald-100 rounded-2xl animate-in slide-in-from-bottom-4">
            <div className="flex items-center mb-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2" />
              <h3 className="text-lg font-bold">{t('wizard.step_target.confirmed')}</h3>
            </div>

            {selectedTable && (
              <p className="text-sm text-slate-600 mb-4">
                <span className="font-medium">{t('wizard.step_target.sheet_name')}:</span>{' '}
                <span className="font-mono text-emerald-700">{resolvedFileName('')}</span>
              </p>
            )}

            {/* syncMode */}
            <label className="text-sm font-medium block mb-3">{t('wizard.step_target.sync_strategy')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {[{ value: 'append', color: 'emerald' }, { value: 'overwrite', color: 'red' }]
                .map(({ value: mode, color }) => (
                  <label key={mode}
                    className={`relative p-4 border rounded-xl cursor-pointer transition-all ${watch('syncMode') === mode
                      ? `border-${color}-600 bg-white shadow-sm ring-1 ring-${color}-600`
                      : 'bg-white hover:bg-slate-50 border-slate-200'}`}>
                    <input type="radio" value={mode} {...register('syncMode')} className="sr-only" />
                    <span className="font-bold text-sm text-slate-800">{t(`wizard.sync_modes.${mode}`)}</span>
                    <p className="text-xs text-slate-500 mt-1">{t(`wizard.sync_modes.${mode}_desc`)}</p>
                    {watch('syncMode') === mode && <CheckCircle2 className={`absolute top-3 right-3 w-4 h-4 text-${color}-600`} />}
                  </label>
                ))}
            </div>

            {watch('syncMode') === 'append' && (
              <div className="mb-4 p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-800 rounded-r-lg flex items-start animate-in fade-in">
                <AlertTriangle className="w-5 h-5 mr-3 shrink-0" />
                <div>
                  <p className="font-bold text-sm">{t('wizard.alerts.append_title')}</p>
                  <p className="text-sm mt-1">{t('wizard.alerts.append_desc_flat')}</p>
                </div>
              </div>
            )}
            {watch('syncMode') === 'overwrite' && (
              <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-800 rounded-r-lg flex items-start animate-in fade-in">
                <AlertTriangle className="w-5 h-5 mr-3 shrink-0" />
                <div>
                  <p className="font-bold text-sm">⚠️ {t('wizard.alerts.overwrite_title')}</p>
                  <p className="text-sm mt-1">{t('wizard.alerts.overwrite_desc_flat')}</p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-emerald-200">
              <button type="button" onClick={handleBack}
                className="px-5 py-2 hover:bg-emerald-100 rounded-lg flex items-center text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" />{t('common.buttons.back')}
              </button>
              <button type="button" onClick={handleContinue} disabled={isTesting}
                className="px-6 py-2 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 flex items-center text-sm disabled:opacity-60">
                {isTesting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
                {t('wizard.step_target.continue_btn')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* ── Panel éxito BD (SQL / NOSQL) ─────────────────────── */}
        {connectionSuccess && !isTargetFlat && (
          <div className="mt-5 p-6 bg-emerald-50 border border-emerald-100 rounded-2xl animate-in slide-in-from-bottom-4">

            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold flex items-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2" />
                {t('wizard.step_target.confirmed')}
              </h3>
              {showDatabaseForm && (
                <button type="submit" disabled={isTesting}
                  className="text-xs font-bold text-emerald-600 hover:text-emerald-800 flex items-center">
                  {isTesting
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <RefreshCw className="w-3 h-3 mr-1" />}
                  {t('wizard.step_source.refresh_tables')}
                </button>
              )}
            </div>

            {/* MongoDB colección vacía/nueva */}
            {isDynamicSchema && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>{t('wizard.alerts.dynamic_schema_title') || 'Colección nueva o vacía'}</strong>
                  {' — '}
                  {t('wizard.alerts.dynamic_schema_desc') || 'MongoDB creará los campos automáticamente al ejecutar la migración.'}
                </p>
              </div>
            )}

            {/* Selector de tabla */}
            {showTableSelect && (
              <div className="mb-6">
                <label className="text-sm font-medium block mb-2">{t('wizard.step_target.target_table')}</label>
                <select value={selectedTable} onChange={(e) => handleTableSelect(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-emerald-200 outline-none bg-white">
                  <option value="" disabled>{t('wizard.step_source.choose_table')}</option>
                  {selectedTable && !availableTables.includes(selectedTable) && (
                    <option value={selectedTable}>{selectedTable}</option>
                  )}
                  {availableTables.map(tbl => <option key={tbl} value={tbl}>{tbl}</option>)}
                </select>
              </div>
            )}

            {/* UI MULTI-TABLA DESTINO — solo BD */}
            {connectionSuccess && !isTargetFlat && (
              <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg mb-6">
                <label className="text-sm font-medium mb-3 block text-slate-700">
                  {t('wizard.step_target.selected_tables') || 'Tablas destino seleccionadas'}
                </label>
                {targetSelectedTables.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {targetSelectedTables.map((table, idx) => (
                      <div key={idx}
                        className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 hover:border-emerald-300">
                        <span className="font-medium text-slate-700">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-600 text-white text-xs mr-2">
                            {idx + 1}
                          </span>
                          {table}
                        </span>
                        <button type="button" onClick={() => removeTargetTable(idx)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium">
                          {t('wizard.delete') || 'Eliminar'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button type="button"
                  onClick={() => {
                    if (selectedTable && !targetSelectedTables.includes(selectedTable)) {
                      addTargetTable(selectedTable); setSelectedTable('');
                    }
                  }}
                  disabled={!selectedTable}
                  className="w-full px-4 py-2 rounded-lg border-2 border-dashed border-emerald-400 text-emerald-600 font-medium hover:bg-emerald-100 disabled:opacity-50">
                  + {t('wizard.add_target_table') || 'Agregar tabla destino'}
                </button>
                {targetSelectedTables.length > 1 && sourceSelectedTables.length > 1 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    ✓ {t('wizard.n_m_mode_ready') || 'Modo N:M listo - Presiona Continuar para definir relaciones'}
                  </div>
                )}
              </div>
            )}

            {/* Estrategia de sincronización */}
            <label className="text-sm font-medium block mb-3">{t('wizard.step_target.sync_strategy')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {[
                { value: 'append', color: 'emerald' },
                { value: 'overwrite', color: 'red' },
                { value: 'upsert', color: 'blue' },
              ].filter(({ value }) => !(isTargetFlat && value === 'upsert'))
                .filter(({ value }) => !(isAppendOnly && value !== 'append'))
                .map(({ value: mode, color }) => (
                  <label key={mode}
                    className={`relative p-4 border rounded-xl cursor-pointer transition-all ${watch('syncMode') === mode
                      ? `border-${color}-600 bg-white shadow-sm ring-1 ring-${color}-600`
                      : 'bg-white hover:bg-slate-50 border-slate-200'}`}>
                    <input type="radio" value={mode} {...register('syncMode')} className="sr-only" />
                    <span className="font-bold text-sm text-slate-800">{t(`wizard.sync_modes.${mode}`)}</span>
                    <p className="text-xs text-slate-500 mt-1">{t(`wizard.sync_modes.${mode}_desc`)}</p>
                    {watch('syncMode') === mode && <CheckCircle2 className={`absolute top-3 right-3 w-4 h-4 text-${color}-600`} />}
                  </label>
                ))}
            </div>

            {watch('syncMode') === 'append' && (
              <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-800 rounded-r-lg flex items-start animate-in fade-in">
                <AlertTriangle className="w-5 h-5 mr-3 shrink-0" />
                <div>
                  <p className="font-bold text-sm">{t('wizard.alerts.append_title')}</p>
                  <p className="text-sm mt-1">{t('wizard.alerts.append_desc')}</p>
                </div>
              </div>
            )}
            {watch('syncMode') === 'overwrite' && (
              <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-800 rounded-r-lg flex items-start animate-in fade-in">
                <AlertTriangle className="w-5 h-5 mr-3 shrink-0" />
                <div>
                  <p className="font-bold text-sm">⚠️ {t('wizard.alerts.overwrite_title')}</p>
                  <p className="text-sm mt-1">{t('wizard.alerts.overwrite_desc')}</p>
                </div>
              </div>
            )}
            {watch('syncMode') === 'upsert' && (
              <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg flex items-start animate-in fade-in">
                <AlertTriangle className="w-5 h-5 mr-3 shrink-0" />
                <div>
                  <p className="font-bold text-sm">⚠️ {t('wizard.alerts.upsert_title')}</p>
                  <p className="text-sm mt-1">{t('wizard.alerts.upsert_desc')}</p>
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-between border-t border-emerald-200 pt-6 mt-6">
              <button type="button" onClick={handleBack}
                className="px-5 py-2 hover:bg-emerald-100 rounded-lg flex items-center">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.buttons.back')}
              </button>
              <button type="button" onClick={handleContinue}
                disabled={isTesting || (!isDynamicSchema && !selectedTable && targetSelectedTables.length === 0 && !['csv', 'dbf'].includes(selectedEngine))}
                className="px-6 py-2 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 flex items-center">
                {isTesting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
                {t('wizard.step_target.continue_btn')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

      </form>
    </div>
  );
}
